/**
 * Fund Deposits Component
 * Lists and manages sponsor fund deposits with allocation tracking
 * Trust Marketplace - Phase 2: Financial Ledger System
 */

import React, { useState, useEffect } from 'react';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { CapabilityGate } from '../common/CapabilityGate';
import { getActiveCboId } from '@/services/api/organizations';
import { getWeaveinApiUrl } from '@/src/config';
import { BankAccountSelector } from './BankAccountSelector';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import {
  DollarSign,
  TrendingUp,
  Calendar,
  Building2,
  CheckCircle,
  AlertCircle,
  Eye,
  Plus,
  Filter,
  Download,
  CreditCard
} from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface FundDepositsProps {
  sponsorId?: number;
  programId?: number;
}

interface FundDeposit {
  id: number;
  deposit_number: string;
  sponsor_id: number;
  sponsor_name?: string;
  program_id?: number;
  program_name?: string;
  deposit_amount: number;
  deposit_date: string;
  deposit_method: string;
  allocated_amount: number;
  disbursed_amount: number;
  remaining_balance: number;
  status: string;
  reference_number?: string;
  notes?: string;
  created_at: string;
  org_bank_account_id?: number;
  bank_account_name?: string;
  bank_account_last4?: string;
}

const DEPOSIT_METHODS = [
  { value: 'WIRE', label: 'Wire Transfer' },
  { value: 'ACH', label: 'ACH' },
  { value: 'CHECK', label: 'Check' },
  { value: 'CARD', label: 'Card' }
];

const DEPOSIT_STATUSES = [
  { value: 'RECEIVED', label: 'Received' },
  { value: 'ALLOCATED', label: 'Allocated' },
  { value: 'PARTIALLY_DISBURSED', label: 'Partially Disbursed' },
  { value: 'FULLY_DISBURSED', label: 'Fully Disbursed' },
  { value: 'RECONCILED', label: 'Reconciled' }
];

const FundDepositsComponent: React.FC<FundDepositsProps> = ({ sponsorId, programId }) => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const { fmt } = useFormattedDate();
  const cboId = getActiveCboId();
  const [deposits, setDeposits] = useState<FundDeposit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterSponsor, setFilterSponsor] = useState<string>(sponsorId?.toString() || '');
  const [filterMethod, setFilterMethod] = useState<string>('');

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState<FundDeposit | null>(null);

  // Create form state
  const [newDeposit, setNewDeposit] = useState({
    sponsor_id: sponsorId || 0,
    program_id: programId || 0,
    deposit_amount: '',
    deposit_date: new Date().toISOString().split('T')[0],
    deposit_method: 'WIRE',
    reference_number: '',
    notes: '',
    org_bank_account_id: null as number | null
  });

  useEffect(() => {
    fetchDeposits();
  }, [sponsorId, programId, filterStatus, filterSponsor, filterMethod]);

  const fetchDeposits = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterSponsor) params.append('sponsor_id', filterSponsor);
      if (programId) params.append('program_id', programId.toString());
      if (filterStatus) params.append('status', filterStatus);
      if (filterMethod) params.append('deposit_method', filterMethod);

      const url = getWeaveinApiUrl(`/api/sponsor-fund-deposits?${params.toString()}`);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch deposits: ${response.status}`);
      }

      const data = await response.json();
      setDeposits(data);
    } catch (err) {
      console.error('Error fetching deposits:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const createDeposit = async () => {
    try {
      const url = getWeaveinApiUrl('/api/sponsor-fund-deposits');

      const response = await authFetch(url, {
        method: 'POST',
        jsonBody: {
          ...newDeposit,
          deposit_amount: parseFloat(newDeposit.deposit_amount)
        }
      });

      if (!response.ok) {
        throw new Error('Failed to create deposit');
      }

      // Reset form and close modal
      setNewDeposit({
        sponsor_id: sponsorId || 0,
        program_id: programId || 0,
        deposit_amount: '',
        deposit_date: new Date().toISOString().split('T')[0],
        deposit_method: 'WIRE',
        reference_number: '',
        notes: '',
        org_bank_account_id: null
      });
      setIsCreateModalOpen(false);
      fetchDeposits();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create deposit');
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status.toUpperCase()) {
      case 'RECEIVED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ALLOCATED':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'PARTIALLY_DISBURSED':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'FULLY_DISBURSED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'RECONCILED':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return fmt.medium(dateString);
  };

  const calculateUtilization = (deposit: FundDeposit): number => {
    if (deposit.deposit_amount === 0) return 0;
    return Math.round((deposit.disbursed_amount / deposit.deposit_amount) * 100);
  };

  // Calculate summary metrics
  const totalDeposited = deposits.reduce((sum, d) => sum + d.deposit_amount, 0);
  const totalAllocated = deposits.reduce((sum, d) => sum + d.allocated_amount, 0);
  const totalDisbursed = deposits.reduce((sum, d) => sum + d.disbursed_amount, 0);
  const totalRemaining = deposits.reduce((sum, d) => sum + d.remaining_balance, 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <p className="text-red-800">Error: {error}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && <h2 className="text-2xl font-bold text-gray-900">{t('financial.fund_deposits', 'Fund Deposits')}</h2>}
          <p className="text-sm text-gray-600 mt-1">{t('financial.track_sponsor_fund_receipts_and', 'Track sponsor fund receipts and allocations')}</p>
        </div>

        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button className="bg-v2accent hover:bg-v2accent/90">
              <Plus className="w-4 h-4 mr-2" />
              Record Deposit
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Record New Fund Deposit</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="deposit_amount">{t('financial.deposit_amount', 'Deposit Amount *')}</Label>
                  <Input
                    id="deposit_amount"
                    type="number"
                    step="0.01"
                    value={newDeposit.deposit_amount}
                    onChange={(e) => setNewDeposit({ ...newDeposit, deposit_amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label htmlFor="deposit_date">{t('financial.deposit_date', 'Deposit Date *')}</Label>
                  <Input
                    id="deposit_date"
                    type="date"
                    value={newDeposit.deposit_date}
                    onChange={(e) => setNewDeposit({ ...newDeposit, deposit_date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <BankAccountSelector
                  cboId={cboId}
                  selectedAccountId={newDeposit.org_bank_account_id ?? undefined}
                  onSelectAccount={(accountId) => setNewDeposit({ ...newDeposit, org_bank_account_id: accountId })}
                  purpose="DEPOSIT"
                  label={t('financial.receiving_bank_account_1', 'Receiving Bank Account *')}
                />
                <p className="text-xs text-gray-600 mt-1">
                  {t('financial.select_which_bank_account_will', 'Select which bank account will receive this deposit')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="deposit_method">{t('financial.deposit_method', 'Deposit Method *')}</Label>
                  <Select
                    value={newDeposit.deposit_method}
                    onValueChange={(value) => setNewDeposit({ ...newDeposit, deposit_method: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPOSIT_METHODS.map(method => (
                        <SelectItem key={method.value} value={method.value}>
                          {method.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="reference_number">{t('financial.reference_number', 'Reference Number')}</Label>
                  <Input
                    id="reference_number"
                    value={newDeposit.reference_number}
                    onChange={(e) => setNewDeposit({ ...newDeposit, reference_number: e.target.value })}
                    placeholder="e.g., Wire confirmation"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes">{t('financial.notes', 'Notes')}</Label>
                <Textarea
                  id="notes"
                  value={newDeposit.notes}
                  onChange={(e) => setNewDeposit({ ...newDeposit, notes: e.target.value })}
                  placeholder={t('financial.additional_notes_about_this_deposit', 'Additional notes about this deposit...')}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                  {t('financial.cancel', 'Cancel')}
                </Button>
                <Button
                  onClick={createDeposit}
                  disabled={!newDeposit.deposit_amount || !newDeposit.deposit_date}
                  className="bg-v2accent hover:bg-v2accent/90"
                >
                  Record Deposit
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-700 font-medium">{t('financial.total_deposited', 'Total Deposited')}</span>
          </div>
          <div className="text-2xl font-bold text-blue-900">{formatCurrency(totalDeposited)}</div>
          <div className="text-xs text-blue-600 mt-1">{deposits.length} deposits</div>
        </Card>

        <Card className="p-4 bg-purple-50 border-purple-200">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-purple-600" />
            <span className="text-xs text-purple-700 font-medium">{t('financial.allocated', 'Allocated')}</span>
          </div>
          <div className="text-2xl font-bold text-purple-900">{formatCurrency(totalAllocated)}</div>
          <div className="text-xs text-purple-600 mt-1">
            {totalDeposited > 0 ? Math.round((totalAllocated / totalDeposited) * 100) : 0}% of total
          </div>
        </Card>

        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700 font-medium">{t('financial.disbursed', 'Disbursed')}</span>
          </div>
          <div className="text-2xl font-bold text-green-900">{formatCurrency(totalDisbursed)}</div>
          <div className="text-xs text-green-600 mt-1">
            {totalDeposited > 0 ? Math.round((totalDisbursed / totalDeposited) * 100) : 0}% utilized
          </div>
        </Card>

        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-4 h-4 text-yellow-600" />
            <span className="text-xs text-yellow-700 font-medium">{t('financial.remaining', 'Remaining')}</span>
          </div>
          <div className="text-2xl font-bold text-yellow-900">{formatCurrency(totalRemaining)}</div>
          <div className="text-xs text-yellow-600 mt-1">available funds</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="filter_status">{t('financial.status', 'Status')}</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t('financial.all_statuses_1', 'All Statuses')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t('financial.all_statuses', 'All Statuses')}</SelectItem>
                {DEPOSIT_STATUSES.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="filter_method">{t('financial.method', 'Method')}</Label>
            <Select value={filterMethod} onValueChange={setFilterMethod}>
              <SelectTrigger>
                <SelectValue placeholder={t('financial.all_methods_1', 'All Methods')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t('financial.all_methods', 'All Methods')}</SelectItem>
                {DEPOSIT_METHODS.map(method => (
                  <SelectItem key={method.value} value={method.value}>
                    {method.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2 flex items-end gap-2">
            <Button variant="outline" onClick={fetchDeposits} className="flex-1">
              <Filter className="w-4 h-4 mr-2" />
              {t('financial.apply_filters', 'Apply Filters')}
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              {t('financial.export', 'Export')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Deposits List */}
      {deposits.length === 0 ? (
        <Card className="p-12 text-center">
          <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('financial.no_deposits_found', 'No deposits found')}</h3>
          <p className="text-sm text-gray-600 mb-4">
            Record your first fund deposit to start tracking
          </p>
          <Button onClick={() => setIsCreateModalOpen(true)} className="bg-v2accent hover:bg-v2accent/90">
            <Plus className="w-4 h-4 mr-2" />
            Record First Deposit
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {deposits.map(deposit => {
            const utilization = calculateUtilization(deposit);

            return (
              <Card key={deposit.id} className="p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100">
                    <DollarSign className="w-6 h-6 text-blue-600" />
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {formatCurrency(deposit.deposit_amount)}
                          </h4>
                          <Badge className={getStatusColor(deposit.status)}>
                            {deposit.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <span>{deposit.deposit_number}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(deposit.deposit_date)}
                          </span>
                          {deposit.sponsor_name && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {deposit.sponsor_name}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedDeposit(deposit)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {t('financial.details', 'Details')}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>{t('financial.deposit_details', 'Deposit Details')}</DialogTitle>
                          </DialogHeader>
                          {selectedDeposit && selectedDeposit.id === deposit.id && (
                            <div className="space-y-4 mt-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-xs text-gray-500">{t('financial.deposit_number', 'Deposit Number')}</Label>
                                  <p className="font-medium">{selectedDeposit.deposit_number}</p>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500">{t('financial.status_1', 'Status')}</Label>
                                  <Badge className={getStatusColor(selectedDeposit.status)}>
                                    {selectedDeposit.status.replace('_', ' ')}
                                  </Badge>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500">{t('financial.deposit_method_1', 'Deposit Method')}</Label>
                                  <p className="font-medium">{selectedDeposit.deposit_method}</p>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500">{t('financial.deposit_date_1', 'Deposit Date')}</Label>
                                  <p className="font-medium">{formatDate(selectedDeposit.deposit_date)}</p>
                                </div>
                                {selectedDeposit.bank_account_name && (
                                  <div className="col-span-2">
                                    <Label className="text-xs text-gray-500">{t('financial.receiving_bank_account', 'Receiving Bank Account')}</Label>
                                    <p className="font-medium">
                                      {selectedDeposit.bank_account_name}
                                      {selectedDeposit.bank_account_last4 && (
                                        <span className="text-gray-500 ml-2">(...{selectedDeposit.bank_account_last4})</span>
                                      )}
                                    </p>
                                  </div>
                                )}
                                {selectedDeposit.reference_number && (
                                  <div className="col-span-2">
                                    <Label className="text-xs text-gray-500">{t('financial.reference_number_1', 'Reference Number')}</Label>
                                    <p className="font-medium">{selectedDeposit.reference_number}</p>
                                  </div>
                                )}
                                {selectedDeposit.notes && (
                                  <div className="col-span-2">
                                    <Label className="text-xs text-gray-500">{t('financial.notes_1', 'Notes')}</Label>
                                    <p className="text-sm text-gray-700">{selectedDeposit.notes}</p>
                                  </div>
                                )}
                              </div>

                              <div className="border-t pt-4">
                                <h4 className="font-semibold mb-3">{t('financial.financial_breakdown', 'Financial Breakdown')}</h4>
                                <div className="space-y-2">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">{t('financial.deposit_amount_1', 'Deposit Amount:')}</span>
                                    <span className="font-semibold">{formatCurrency(selectedDeposit.deposit_amount)}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">{t('financial.allocated_1', 'Allocated:')}</span>
                                    <span className="font-semibold text-purple-600">
                                      {formatCurrency(selectedDeposit.allocated_amount)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">{t('financial.disbursed_1', 'Disbursed:')}</span>
                                    <span className="font-semibold text-green-600">
                                      {formatCurrency(selectedDeposit.disbursed_amount)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm border-t pt-2">
                                    <span className="text-gray-600 font-medium">{t('financial.remaining_1', 'Remaining:')}</span>
                                    <span className="font-bold text-lg">
                                      {formatCurrency(selectedDeposit.remaining_balance)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                    </div>

                    {/* Financial Breakdown */}
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('financial.allocated_2', 'Allocated')}</div>
                        <div className="text-sm font-semibold text-purple-600">
                          {formatCurrency(deposit.allocated_amount)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('financial.disbursed_2', 'Disbursed')}</div>
                        <div className="text-sm font-semibold text-green-600">
                          {formatCurrency(deposit.disbursed_amount)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('financial.remaining_2', 'Remaining')}</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(deposit.remaining_balance)}
                        </div>
                      </div>
                    </div>

                    {/* Utilization Bar */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">{t('financial.utilization', 'Utilization')}</span>
                        <span className="text-xs font-semibold text-gray-900">{utilization}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all"
                          style={{ width: `${utilization}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Method Badge */}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <CreditCard className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {DEPOSIT_METHODS.find(m => m.value === deposit.deposit_method)?.label}
                      </span>
                      {deposit.bank_account_name && (
                        <>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">
                            {deposit.bank_account_name}
                            {deposit.bank_account_last4 && ` (...${deposit.bank_account_last4})`}
                          </span>
                        </>
                      )}
                      {deposit.reference_number && (
                        <>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">Ref: {deposit.reference_number}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Wrap with capability gate
const FundDeposits: React.FC<FundDepositsProps> = (props) => {
  const cboId = getActiveCboId();

  return (
    <CapabilityGate
      organizationId={cboId}
      capability="financial_management"
      behavior="show-upgrade"
      upgradeMessage="Financial Management (Fund Deposits & Reconciliation) is not enabled for your organization. Contact your administrator to enable this premium feature."
    >
      <FundDepositsComponent {...props} />
    </CapabilityGate>
  );
};

export default FundDeposits;
